/**
 * Product Search Component
 * 
 * A reusable custom element that filters products by title.
 * 
 * Attributes:
 *   data-product-selector - CSS selector for product cards (default: '[data-product-card]')
 *   data-title-selector - CSS selector for product title within card (default: '[data-product-title]')
 *   data-hidden-class - Class to add when hiding products (default: 'hidden-by-search')
 *   data-container-selector - Optional selector for search scope container (default: document)
 * 
 * Events:
 *   product-search:filter - Dispatched when search filters products
 *     detail: { query: string, visibleCount: number, totalCount: number }
 *   product-search:clear - Dispatched when search is cleared
 */

if (!customElements.get('product-search-component')) {
  class ProductSearchComponent extends HTMLElement {
    constructor() {
      super();
      
      /** @type {HTMLInputElement|null} */
      this.input = null;
      /** @type {HTMLButtonElement|null} */
      this.clearBtn = null;
      /** @type {string} */
      this.productSelector = '[data-product-card]';
      /** @type {string} */
      this.titleSelector = '[data-product-title]';
      /** @type {string} */
      this.hiddenClass = 'hidden-by-search';
      /** @type {string} */
      this.containerSelector = '';
      
      // Bind methods
      this.handleInput = this.handleInput.bind(this);
      this.handleClear = this.handleClear.bind(this);
      this.handleKeydown = this.handleKeydown.bind(this);
    }

    connectedCallback() {
      // Get configuration from attributes
      this.productSelector = this.dataset.productSelector || '[data-product-card]';
      this.titleSelector = this.dataset.titleSelector || '[data-product-title]';
      this.hiddenClass = this.dataset.hiddenClass || 'hidden-by-search';
      this.containerSelector = this.dataset.containerSelector || '';
      
      // Get elements
      this.input = this.querySelector('[data-search-input]');
      this.clearBtn = this.querySelector('[data-search-clear]');
      
      if (!this.input) {
        console.warn('ProductSearchComponent: Missing input element [data-search-input]');
        return;
      }
      
      // Attach event listeners
      this.input.addEventListener('input', this.handleInput);
      this.input.addEventListener('keydown', this.handleKeydown);
      this.clearBtn?.addEventListener('click', this.handleClear);
    }

    disconnectedCallback() {
      // Clean up event listeners
      this.input?.removeEventListener('input', this.handleInput);
      this.input?.removeEventListener('keydown', this.handleKeydown);
      this.clearBtn?.removeEventListener('click', this.handleClear);
    }

    /**
     * Gets the container element to search within
     * @returns {Element|Document}
     */
    getContainer() {
      if (this.containerSelector) {
        return document.querySelector(this.containerSelector) || document;
      }
      return document;
    }

    /**
     * Handles input event on search field
     * @param {Event} event
     */
    handleInput(event) {
      const query = /** @type {HTMLInputElement} */ (event.target).value.trim().toLowerCase();
      
      // Show/hide clear button
      if (this.clearBtn) {
        this.clearBtn.classList.toggle('hidden', query.length === 0);
      }
      
      this.filterProducts(query);
    }

    /**
     * Handles keydown event for escape key
     * @param {KeyboardEvent} event
     */
    handleKeydown(event) {
      if (event.key === 'Escape' && this.input?.value) {
        this.clear();
      }
    }

    /**
     * Handles clear button click
     */
    handleClear() {
      this.clear();
    }

    /**
     * Clears the search input and shows all products
     */
    clear() {
      if (this.input) {
        this.input.value = '';
        this.input.focus();
      }
      this.clearBtn?.classList.add('hidden');
      this.filterProducts('');
      
      // Dispatch clear event
      this.dispatchEvent(new CustomEvent('product-search:clear', {
        bubbles: true
      }));
    }

    /**
     * Filters products based on search query
     * @param {string} query - Search query (already lowercased)
     */
    filterProducts(query) {
      const container = this.getContainer();
      const productCards = container.querySelectorAll(this.productSelector);
      let visibleCount = 0;
      const totalCount = productCards.length;
      
      productCards.forEach((card) => {
        const titleEl = card.querySelector(this.titleSelector);
        const title = titleEl?.textContent?.trim().toLowerCase() || '';
        
        if (query === '' || title.includes(query)) {
          card.classList.remove(this.hiddenClass);
          visibleCount++;
        } else {
          card.classList.add(this.hiddenClass);
        }
      });
      
      // Handle no results message if exists
      const noResultsEl = container.querySelector('[data-search-no-results]');
      if (noResultsEl) {
        noResultsEl.classList.toggle('hidden', visibleCount > 0 || query === '');
      }
      
      // Update products count wrapper
      this.updateProductsCount(visibleCount, totalCount, query);
      
      // Dispatch filter event
      this.dispatchEvent(new CustomEvent('product-search:filter', {
        bubbles: true,
        detail: {
          query,
          visibleCount,
          totalCount
        }
      }));
    }
  
    /**
     * Updates the count text for a wrapper element
     * @param {Element} wrapper - The wrapper element
     * @param {number} start - The start index
     * @param {number} end - The end index
     * @param {number} total - The total number of products
     */
    updateCountText(wrapper, start, end, total) {
      const template = wrapper.getAttribute('data-count-template');
    
      if (!template) return;
    
      wrapper.setAttribute('data-count-start', String(start));
      wrapper.setAttribute('data-count-end', String(end));
      wrapper.setAttribute('data-count-total', String(total));
    
      wrapper.textContent = template
        .replace('__START__', start.toString())
        .replace('__END__', end.toString())
        .replace('__TOTAL__', total);
    }
    /**
     * Updates the products count wrapper elements
     * @param {number} visibleCount - Number of visible products
     * @param {number} totalCount - Total number of products
     * @param {string} query - Search query
     */
    
    updateProductsCount(visibleCount, totalCount, query) {
      const countWrappers = document.querySelectorAll('[data-count-wrapper]');
      
      countWrappers.forEach((wrapper) => {
        if (!wrapper.hasAttribute('data-original-text')) {
          wrapper.setAttribute('data-original-text', wrapper.textContent);
        }
        
        if (query !== '') {
          const start = visibleCount > 0 ? 1 : 0;
          var total = totalCount != null ? totalCount.toString() : (wrapper.getAttribute('data-count-total') || '0');

          wrapper.setAttribute('data-count-start', start.toString());
          wrapper.setAttribute('data-count-end', visibleCount.toString());
          this.updateCountText(wrapper, start, visibleCount, total.toString());  
        } else {
          const originalText = wrapper.getAttribute('data-original-text');
          if (originalText) {
            wrapper.textContent = originalText;
            wrapper.removeAttribute('data-original-text');
          }
        }
      });
    }

    /**
     * Gets the current search query
     * @returns {string}
     */
    getQuery() {
      return this.input?.value.trim().toLowerCase() || '';
    }

    /**
     * Sets the search query programmatically
     * @param {string} query
     */
    setQuery(query) {
      if (this.input) {
        this.input.value = query;
        this.handleInput({ target: this.input });
      }
    }
  }

  customElements.define('product-search-component', ProductSearchComponent);
}
